import React, { useEffect, useMemo, useState } from 'react'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { getData, getProductRecord, setTableData } from './store/dataSlice'
import { HiDotsVertical, HiOutlinePencil, HiTrash } from 'react-icons/hi'
import Tooltip from 'components/Ui/Tooltip'
import { Dropdown, Modal, Table } from 'components/Ui'
import { Link, useParams } from 'react-router-dom'
import { Button, FileUpload } from 'components/Form'
import { deleteApiRecord, bulkDeleteApiRecords } from 'services/SerialService'
import { Success, Error } from 'utils/alerts'
import { Field, Form, Formik } from 'formik'
import { attachInvoiceApi, getInvoiceDocApi, verifyInvoiceApi } from 'services/ProductService'
import * as Yup from 'yup'
injectReducer('serial', reducer)

const { REACT_APP_URL } = process.env

const validationSchema = Yup.object().shape({
    image: Yup.string().required('this field is required'),
})

export default function Products() {
    const dispatch = useDispatch()
    const { productId } = useParams()
    const { data, tableData, loading } = useSelector(state => state.serial.data)
    const [openAttachInvoiceModal, setOpenAttachInvoiceModal] = useState(false)
    const [openVerifyInvoiceModal, setOpenVerifyInvoiceModal] = useState(false)
    const [loadings, setLoading] = useState(false)
    const [userProductId, setUserProductId] = useState(0)
    const [invoiceDoc, setInvoiceDoc] = useState([])
    const [selectedIds, setSelectedIds] = useState([])
    const [isDeleting, setIsDeleting] = useState(false)

    useEffect(() => {
        fetchData()
        dispatch(getProductRecord(productId))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, tableData.sort, tableData.query, tableData.pageIndex, tableData.pageSize, tableData.selectedTab, tableData.registration_status])

    const fetchData = () => {
        const params = { ...tableData, product_id: productId }
        if (tableData.registration_status) {
            params.registration_status = tableData.registration_status
        }
        dispatch(getData(params))
    }

    const handleUpload = (data, setSubmitting) => {
        setSubmitting(true)
        attachInvoiceApi(data)
            .then(() => {
                Success('Upload Successfully!')
                setOpenAttachInvoiceModal(false)
                setSubmitting(false)
            })
            .catch(error => console.log('Error: ', error))
    }

    const handleVerify = data => {
        verifyInvoiceApi(data)
            .then(() => {
                Success(data.product_verified ? 'Verified Successfully!' : 'Rejected Successfully!')
                fetchData()
                setOpenVerifyInvoiceModal(false)
                setInvoiceDoc([])
                setLoading(false)
            })
            .catch(error => console.log('Error: ', error))
    }

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) {
            Error('Please select items to delete')
            return
        }

        const confirmed = window.confirm('Are you sure you want to delete the selected serials?')
        if (!confirmed) return

        setIsDeleting(true)
        try {
            await bulkDeleteApiRecords(selectedIds)
            Success(`Successfully deleted ${selectedIds.length} serial(s)`)
            setSelectedIds([])
            fetchData()
        } catch (error) {
            Error('Failed to delete some items')
            console.error('Delete error:', error)
        } finally {
            setIsDeleting(false)
        }
    }

    const columns = useMemo(
        () => [
            {
                field: 'user',
                header: 'Customer',
                className: 'min-w-[120px]',
                renderCell: row => {
                    const firstName = row?.user?.first_name || ''
                    const lastName = row?.user?.last_name || ''
                    const fullName = `${firstName} ${lastName}`.trim()
                    return (
                        <div className="truncate" title={fullName || 'N/A'}>
                            {fullName || 'N/A'}
                        </div>
                    )
                }
            },
            {
                field: 'registration_status',
                header: 'Status',
                className: 'min-w-[90px]',
                renderCell: row => {
                    const status = row?.user?.registration_status
                    const statusColors = {
                        'complete': 'bg-green-100 text-green-800 border-green-200',
                        'incomplete': 'bg-yellow-100 text-yellow-800 border-yellow-200'
                    }
                    const statusIcons = {
                        'complete': '✓',
                        'incomplete': '⚠️'
                    }
                    
                    return (
                        <Link 
                            to={`/user?registration_status=${status || 'all'}`}
                            className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium border hover:opacity-80 transition-opacity cursor-pointer ${statusColors[status] || 'bg-gray-100 text-gray-800 border-gray-200'}`}
                        >
                            <span className="mr-1">{statusIcons[status] || '?'}</span>
                            {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
                        </Link>
                    )
                }
            },
            {
                field: 'invoice_number',
                header: 'Invoice',
                className: 'min-w-[100px]',
                renderCell: row => {
                    const invoiceNumber = row?.invoice_number || row?.user_product_invoices?.[0]?.invoice_number
                    return invoiceNumber ? (
                        <div className="truncate text-blue-600 font-medium" title={invoiceNumber}>
                            {invoiceNumber}
                        </div>
                    ) : (
                        <span className="text-gray-500 text-xs">-</span>
                    )
                }
            },
            {
                field: 'product_label',
                header: 'Product',
                className: 'min-w-[120px]',
                renderCell: row => {
                    const productName = row?.product_label ?? row?.product?.name
                    return (
                        <div className="truncate" title={productName}>
                            {productName}
                        </div>
                    )
                }
            },
            {
                field: 'batch_number',
                header: 'Batch',
                className: 'min-w-[100px]',
                renderCell: row => (
                    <div className="truncate" title={row.batch_number}>
                        {row.batch_number || '-'}
                    </div>
                )
            },
            {
                field: 'product_verified',
                header: 'P.Verified',
                className: 'min-w-[80px] text-center',
                renderCell: row => (
                    <span className={`inline-block w-2 h-2 rounded-full ${
                        row.product_verified ? 'bg-green-500' : 'bg-red-500'
                    }`} title={row.product_verified ? 'Verified' : 'Not Verified'}></span>
                )
            },
            {
                field: 'warranty_verified',
                header: 'W.Verified',
                className: 'min-w-[80px] text-center',
                renderCell: row => (
                    <span className={`inline-block w-2 h-2 rounded-full ${
                        row.warranty_verified ? 'bg-green-500' : 'bg-red-500'
                    }`} title={row.warranty_verified ? 'Verified' : 'Not Verified'}></span>
                )
            },
            {
                field: 'purchase_date',
                header: 'Purchase',
                className: 'min-w-[90px]',
                renderCell: row => {
                    const date = row.purchase_date
                        ? new Date(row.purchase_date).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: '2-digit',
                          })
                        : '-'
                    return (
                        <div className="text-xs" title={row.purchase_date}>
                            {date}
                        </div>
                    )
                }
            },
            {
                field: 'created_at',
                header: 'Created',
                className: 'min-w-[90px]',
                renderCell: row => {
                    const date = row.created_at
                        ? new Date(row.created_at).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: '2-digit',
                          })
                        : '-'
                    return (
                        <div className="text-xs" title={row.created_at}>
                            {date}
                        </div>
                    )
                }
            },
            {
                field: 'image',
                header: 'Image',
                className: 'min-w-[60px]',
                renderCell: row => {
                    return (
                        <div className="flex items-center justify-center w-10 h-10 overflow-hidden">
                            <a href={row?.user_product_photos[0]?.image ?? row?.product?.image} target="_blank" rel="noreferrer">
                                <img
                                    className="w-full h-full object-cover rounded"
                                    src={
                                        row?.user_product_photos[0]?.image
                                            ? row?.user_product_photos[0]?.image
                                            : row?.product?.image ?? '/images/ypp-logo.png'
                                    }
                                    alt="Product"
                                />
                            </a>
                        </div>
                    )
                },
            },
            {
                header: 'Action',
                className: 'min-w-[120px]',
                renderCell: row => {
                    return (
                        <div className="flex items-center gap-1">
                            <Tooltip title={'Edit'}>
                                <Link
                                    to={`/manage-product/products/serials/${productId}/${row?.id}/edit`}
                                    className="hover:text-primary px-2 py-1"
                                    title="Edit"
                                >
                                    <HiOutlinePencil size={16} />
                                </Link>
                            </Tooltip>
                            <Dropdown>
                                <Dropdown.Trigger downShow={false}>
                                    <Tooltip title={'More'} placement={'top'}>
                                        <span className={`text-sm cursor-pointer hover:text-primary px-2 py-1`}>
                                            <HiDotsVertical size={16} />
                                        </span>
                                    </Tooltip>
                                </Dropdown.Trigger>

                                <Dropdown.Content>
                                    <Dropdown.Link
                                        className={`cursor-pointer hover:text-primary`}
                                        onClick={() => {
                                            setOpenAttachInvoiceModal(true)
                                            setUserProductId(row.id)
                                        }}
                                    >
                                        Attach Invoice
                                    </Dropdown.Link>
                                    <Dropdown.Link
                                        className={`cursor-pointer hover:text-primary`}
                                        onClick={() => {
                                            setOpenVerifyInvoiceModal(true)
                                            setUserProductId(row.id)
                                            getInvoiceDocApi({ user_product_id: row.id })
                                                .then(response => setInvoiceDoc(response.data))
                                                .catch(error => console.log('Error: ', error))
                                        }}
                                    >
                                        Verify Invoice
                                    </Dropdown.Link>
                                </Dropdown.Content>
                            </Dropdown>
                        </div>
                    )
                },
            },
        ],
        // eslint-disable-next-line
        [tableData.sort, tableData.query, tableData.pageIndex, tableData.pageSize, tableData.selectedTab, tableData.company, tableData.registration_status]
    )

    return (
        <>
            <div className="flex flex-col gap-1 mb-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">Serials</h2>
                <div className="flex items-center gap-2">
                    {/* Registration Status Filter */}
                    <div className="flex bg-gray-100 rounded-lg p-1 dark:bg-gray-800">
                        <button
                            onClick={() => dispatch(setTableData({ ...tableData, registration_status: null, pageIndex: 1 }))}
                            className={`px-3 py-1 text-sm rounded-md transition-colors ${
                                !tableData.registration_status 
                                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white' 
                                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                            }`}
                        >
                            All Serials
                        </button>
                        <button
                            onClick={() => dispatch(setTableData({ ...tableData, registration_status: 'incomplete', pageIndex: 1 }))}
                            className={`px-3 py-1 text-sm rounded-md transition-colors ${
                                tableData.registration_status === 'incomplete' 
                                    ? 'bg-yellow-100 text-yellow-800 shadow-sm dark:bg-yellow-900 dark:text-yellow-100' 
                                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                            }`}
                        >
                            <span className="mr-1">⚠️</span>
                            Incomplete ({data?.filter(item => item?.user?.registration_status === 'incomplete').length || 0})
                        </button>
                        <button
                            onClick={() => dispatch(setTableData({ ...tableData, registration_status: 'complete', pageIndex: 1 }))}
                            className={`px-3 py-1 text-sm rounded-md transition-colors ${
                                tableData.registration_status === 'complete' 
                                    ? 'bg-green-100 text-green-800 shadow-sm dark:bg-green-900 dark:text-green-100' 
                                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                            }`}
                        >
                            <span className="mr-1">✓</span>
                            Complete
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Incomplete Registration Notice */}
            {tableData.registration_status === 'incomplete' && (
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg dark:bg-yellow-900/20 dark:border-yellow-800">
                    <div className="flex items-start">
                        <span className="text-yellow-600 mr-2">⚠️</span>
                        <div>
                            <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                                Incomplete Customer Registrations
                            </h3>
                            <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                                These product serials belong to customers with incomplete registrations. Click on the status to complete their registration.
                            </p>
                        </div>
                    </div>
                </div>
            )}
            <div className="bg-white border rounded-sm border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                <Table
                    add_new={`/manage-product/products/serials/${productId}/create`}
                    className={'min-h-[180px]'}
                    filter={false}
                    search_show={false}
                    loading={loading}
                    datas={data || []}
                    columns={columns}
                    pagingData={tableData}
                    onChange={e => dispatch(setTableData(e))}
                    select={true}
                    ids={selectedIds || []}
                    setIds={setSelectedIds}
                    bulkDeleteEnabled={selectedIds.length > 0}
                    onBulkDelete={handleBulkDelete}
                    bulkDeleteLoading={isDeleting}
                    bulkDeleteCount={selectedIds.length}
                />
            </div>
            <Modal
                show={openAttachInvoiceModal}
                onClose={() => {
                    setOpenAttachInvoiceModal(false)
                    setInvoiceDoc([])
                }}
            >
                <div className="p-6">
                    <h2 className="mb-4 font-semibold text-black text-title-md2 dark:text-white">Attach Invoice</h2>
                    <Formik
                        initialValues={{
                            image: '',
                        }}
                        validationSchema={validationSchema}
                        onSubmit={(values, { setSubmitting }) => {
                            handleUpload({ ...values, user_product_id: userProductId, parse_invoice: false }, setSubmitting)
                        }}
                    >
                        {({ values, touched, errors, isSubmitting }) => (
                            <Form className="flex flex-col gap-3">
                                <Field name="image">
                                    {({ field, form }) => (
                                        <FileUpload
                                            label="Image"
                                            className="md:col-span-2"
                                            name="image"
                                            error={errors.image && touched.image}
                                            message={errors.image}
                                            onChange={e => {
                                                form.setFieldValue(field.name, e.target.files[0])
                                            }}
                                        />
                                    )}
                                </Field>
                                <Button
                                    className="w-full mt-3 text-white border-primary bg-primary dark:border-secondary dark:bg-secondary"
                                    disabled={isSubmitting}
                                    type="submit"
                                >
                                    {isSubmitting ? 'Uploading...' : 'Upload'}
                                </Button>
                            </Form>
                        )}
                    </Formik>
                </div>
            </Modal>

            <Modal
                show={openVerifyInvoiceModal}
                onClose={() => {
                    setOpenVerifyInvoiceModal(false)
                    setInvoiceDoc([])
                }}
            >
                <div className="p-6">
                    <h2 className="mb-4 font-semibold text-black text-title-md2 dark:text-white">Verify Invoice</h2>
                    {invoiceDoc[0]?.image ? (
                        <div className="flex justify-center w-full">
                            <div
                                className={`flex items-center justify-center mb-4 overflow-hidden ${invoiceDoc[0]?.image?.includes('.pdf') ? 'text-purple font-bold h-30' : 'w-40 h-40'}`}
                            >
                                <a href={`${REACT_APP_URL}${invoiceDoc[0]?.image}`} target="_blank" rel="noreferrer">
                                    {invoiceDoc[0]?.image?.includes('.pdf') ? (
                                        <Tooltip title={'View'}>
                                            {invoiceDoc[0]?.image?.split('/')[invoiceDoc[0]?.image?.split('/').length - 1]}
                                        </Tooltip>
                                    ) : (
                                        <img src={`${REACT_APP_URL}${invoiceDoc[0]?.image}`} alt="error" />
                                    )}
                                </a>
                            </div>
                        </div>
                    ) : null}
                    <div className="flex gap-3">
                        <Button
                            className="w-full mt-3 text-white border-[#C67C4E] bg-[#C67C4E] dark:border-[#C67C4E] dark:bg-[#C67C4E]"
                            disabled={loadings}
                            type="button"
                            onClick={() => {
                                setLoading(true)
                                handleVerify({
                                    product_verified: false,
                                    user_product_id: userProductId,
                                    warranty_verified: false,
                                })
                            }}
                        >
                            {loadings ? 'loading...' : 'Recapture'}
                        </Button>
                        <Button
                            className="w-full mt-3 text-white border-primary bg-primary dark:border-secondary dark:bg-secondary"
                            disabled={loadings}
                            type="button"
                            onClick={() => {
                                setLoading(true)
                                handleVerify({
                                    product_verified: true,
                                    user_product_id: userProductId,
                                    warranty_verified: true,
                                })
                            }}
                        >
                            {loadings ? 'Verifying...' : 'Verify'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    )
}
